import React, { useEffect, useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  CircularProgress,
  TextField,
  Button,
  Breadcrumbs,
  Link,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  Stack,
  Autocomplete,
  List,
  ListItem,
  ListItemText
} from '@mui/material';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import VisibilityIcon from '@mui/icons-material/Visibility';
import PrintIcon from '@mui/icons-material/Print';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ReceiptIcon from '@mui/icons-material/Receipt';
import CurrencyRupeeIcon from '@mui/icons-material/CurrencyRupee';
import ReplayIcon from '@mui/icons-material/Replay';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import axiosInstance from '../../utils/axios';
import ReportMetricCard from '../../components/reports/ReportMetricCard';
import { reportPageSx } from '../../components/reports/reportPageLayout';
import API_CONFIG from '../../config/api.config';
import Layout from '../../components/layout/Layout';
import { format } from 'date-fns';
import { formatCurrency } from '../../utils/formatCurrency';
import { toast } from 'react-hot-toast';
import * as XLSX from 'xlsx';

const PurchaseReport = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [startDate, setStartDate] = useState(new Date(Date.now() - 30 * 24 * 60 * 60 * 1000));
  const [endDate, setEndDate] = useState(new Date());
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [suppliers, setSuppliers] = useState([]);
  const [purchaseReport, setPurchaseReport] = useState(null);
  const [filteredPurchaseDetails, setFilteredPurchaseDetails] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [expandedOrderId, setExpandedOrderId] = useState(null);
  const [isExporting, setIsExporting] = useState(false);

  // Fetch initial data
  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        setLoading(true);
        const [suppliersRes, reportRes] = await Promise.all([
          axiosInstance.get(API_CONFIG.ENDPOINTS.SUPPLIERS),
          axiosInstance.get(API_CONFIG.ENDPOINTS.PURCHASE_REPORT, {
            params: {
              startDate: format(startDate, 'yyyy-MM-dd'),
              endDate: format(endDate, 'yyyy-MM-dd'),
              supplierId: selectedSupplier?.id
            }
          })
        ]);

        // Handle suppliers (both paginated and non-paginated responses)
        let suppliersData = [];
        if (suppliersRes.data?.content) {
          // Handle paginated response
          suppliersData = suppliersRes.data.content;
          // If we need more suppliers, fetch additional pages
          if (suppliersRes.data.totalPages > 1) {
            const additionalRequests = [];
            for (let page = 1; page < suppliersRes.data.totalPages; page++) {
              additionalRequests.push(
                axiosInstance.get(`${API_CONFIG.ENDPOINTS.SUPPLIERS}?page=${page}&size=1000`)
              );
            }
            const additionalResponses = await Promise.all(additionalRequests);
            additionalResponses.forEach(response => {
              suppliersData = suppliersData.concat(response.data?.content || []);
            });
          }
        } else {
          // Handle non-paginated response
          suppliersData = Array.isArray(suppliersRes.data) ? suppliersRes.data : [];
        }

        // Set suppliers after filtering invalid entries
        setSuppliers(suppliersData.filter(supplier => supplier && supplier.name));

        setPurchaseReport(reportRes.data || {});
        setFilteredPurchaseDetails(reportRes.data?.purchaseDetails || []);
      } catch (error) {
        console.error('Error fetching initial data:', error);
        if (error.response?.status === 404) {
          toast.error('No purchase data found for the selected period');
        } else if (error.response?.status === 403) {
          toast.error('You do not have permission to view purchase reports');
        } else {
          toast.error('Failed to load purchase data');
        }
        // Initialize empty arrays on error
        setSuppliers([]);
        setPurchaseReport({});
        setFilteredPurchaseDetails([]);
      } finally {
        setLoading(false);
      }
    };

    fetchInitialData();
  }, [startDate, endDate]);

  // Add useEffect to handle filtering (simplified since backend now filters)
  useEffect(() => {
    if (purchaseReport?.purchaseDetails) {
      setFilteredPurchaseDetails(purchaseReport.purchaseDetails);
    } else {
      setFilteredPurchaseDetails([]);
    }
  }, [purchaseReport?.purchaseDetails]);

  const fetchPurchaseReport = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(API_CONFIG.ENDPOINTS.PURCHASE_REPORT, {
        params: {
          startDate: format(startDate, 'yyyy-MM-dd'),
          endDate: format(endDate, 'yyyy-MM-dd'),
          supplierId: selectedSupplier?.id
        }
      });

      // Map the purchase details
      const purchasesWithIds = response.data.purchaseDetails?.map(purchase => ({
        ...purchase,
        id: purchase.id || purchase.orderId,
        invoiceNumber: purchase.invoiceNumber,
        invoiceDate: purchase.invoiceDate,
        finalAmount: purchase.finalAmount || purchase.total
      })) || [];

      setPurchaseReport({
        ...response.data,
        purchaseDetails: purchasesWithIds
      });

      setFilteredPurchaseDetails(purchasesWithIds);
      setPage(0);

    } catch (error) {
      console.error('Error fetching purchase report:', error);
      if (error.response?.status === 403) {
        toast.error('You do not have permission to access the purchase report.');
      } else {
        toast.error('Failed to fetch purchase report');
      }
    } finally {
      setLoading(false);
    }
  };

  // Update the handleGenerateReport validation
  const handleGenerateReport = async () => {
    if (!startDate || !endDate) {
      toast.error('Please select both start and end dates');
      return;
    }

    if (new Date(endDate) < new Date(startDate)) {
      toast.error('End date cannot be earlier than start date');
      return;
    }

    await fetchPurchaseReport();
  };

  // Modify the handleSupplierChange to not trigger automatic fetch
  const handleSupplierChange = (_, newValue) => {
    setSelectedSupplier(newValue);
    // Remove the automatic fetch
  };

  const handleViewInvoice = (invoiceId) => {
    window.open(`${API_CONFIG.BASE_URL}/api/invoices/${invoiceId}`, '_blank');
  };

  const handlePrintInvoice = async (orderId) => {
    try {
      console.log('Attempting to download purchase order invoice for ID:', orderId);

      const response = await axiosInstance.get(`/purchase-orders/${orderId}/receipt`, {
        responseType: 'blob',
        headers: {
          'Accept': 'application/pdf'
        }
      });

      console.log('Response received:', response);
      console.log('Response data type:', typeof response.data);
      console.log('Response data size:', response.data?.size || 'unknown');

      // Create a blob from the PDF stream
      const blob = new Blob([response.data], { type: 'application/pdf' });

      // Create a link element
      const link = document.createElement('a');
      link.href = window.URL.createObjectURL(blob);
      link.download = `purchase-order-${orderId}.pdf`;

      // Append to body, click and remove
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Clean up the URL object
      window.URL.revokeObjectURL(link.href);

      toast.success('Invoice downloaded successfully');
    } catch (error) {
      console.error('Error downloading invoice:', error);
      console.error('Error response:', error.response);
      console.error('Error status:', error.response?.status);
      console.error('Error data:', error.response?.data);

      if (error.response?.status === 404) {
        toast.error('Invoice not found');
      } else if (error.response?.status === 403) {
        toast.error('You do not have permission to download this invoice');
      } else if (error.response?.status === 500) {
        toast.error('Server error occurred while generating invoice');
      } else {
        toast.error(`Failed to download invoice: ${error.message || 'Unknown error'}`);
      }
    }
  };

  const handleViewOrder = async (orderId) => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(`/purchase-orders/${orderId}`);
      if (response.data) {
        // Calculate totals from items
        const items = (response.data.items || []).map(item => ({
          ...item,
          productName: item.product?.name || item.productName,
          price: Number(item.unitPrice || item.price || 0),
          quantity: Number(item.quantity || 0),
          total: Number(item.unitPrice || item.price || 0) * Number(item.quantity || 0)
        }));

        // Calculate subtotal from items
        const subtotal = items.reduce((sum, item) => sum + item.total, 0);

        // Calculate tax (assuming tax rate from response or default to 18%)
        const taxRate = Number(response.data.taxRate || 18) / 100;
        const tax = subtotal * taxRate;

        // Calculate final amount
        const discount = Number(response.data.discount || 0);
        const totalAmount = subtotal + tax - discount;

        const orderData = {
          ...response.data,
          orderNumber: response.data.orderNumber || response.data.purchaseOrderNumber,
          supplierName: response.data.supplier?.name || response.data.supplierName,
          supplierEmail: response.data.supplier?.email || response.data.supplierEmail,
          supplierPhone: response.data.supplier?.phone || response.data.supplierPhone,
          supplierAddress: response.data.supplier?.address || response.data.supplierAddress,
          supplierGstNumber: response.data.supplier?.gstNumber || response.data.supplierGstNumber,
          items: items,
          subtotal: Number(subtotal).toFixed(2),
          tax: Number(tax).toFixed(2),
          discount: Number(discount).toFixed(2),
          totalAmount: Number(totalAmount).toFixed(2),
          invoiceNumber: response.data.invoiceNumber || response.data.invoiceNo,
          status: response.data.status || 'PENDING'
        };
        setSelectedOrder(orderData);
        setExpandedOrderId(orderId);
      }
    } catch (error) {
      console.error('Error fetching order details:', error);
      if (error.response?.status === 404) {
        toast.error('Order not found');
      } else {
        toast.error('Failed to load order details');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCloseOrderDetails = () => {
    setSelectedOrder(null);
    setExpandedOrderId(null);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleToggleOrder = (orderId) => {
    setExpandedOrderId(expandedOrderId === orderId ? null : orderId);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'PENDING':
        return 'warning';
      case 'COMPLETED':
        return 'success';
      case 'CANCELLED':
        return 'error';
      default:
        return 'default';
    }
  };

  const handleExportToExcel = async () => {
    try {
      setIsExporting(true);

      // Prepare the summary data
      const summaryData = [
        ['Purchase Report Summary'],
        ['Total Orders', purchaseReport?.totalOrders || 0],
        ['Total Purchases', purchaseReport?.totalPurchases || 0],
        ['Total Returns', purchaseReport?.totalReturns || 0],
        ['Net Spent', purchaseReport?.totalSpent || 0],
        [''],
        ['Purchase Details']
      ];

      // Prepare the table data for export
      const exportData = filteredPurchaseDetails.map((purchase, index) => ({
        'S.No': index + 1,
        'Order Number': purchase.orderNumber,
        'Supplier': purchase.supplier?.name || '-',
        'Order Date': purchase.orderDate ? format(new Date(purchase.orderDate), 'dd/MM/yyyy') : '-',
        'Invoice Number': purchase.invoiceNumber || '-',
        'Invoice Date': purchase.invoiceDate ? format(new Date(purchase.invoiceDate), 'dd/MM/yyyy') : '-',
        'Amount': purchase.finalAmount || purchase.total || 0,
        'Status': purchase.status || '-'
      }));

      // Create workbook
      const wb = XLSX.utils.book_new();

      // Create worksheet starting with metadata and summary
      const ws = XLSX.utils.aoa_to_sheet([
        ['Purchase Report'],
        ['Generated on: ' + format(new Date(), 'dd/MM/yyyy HH:mm:ss')],
        ['Period: ' + format(startDate, 'dd/MM/yyyy') + ' to ' + format(endDate, 'dd/MM/yyyy')],
        [''],
        ...summaryData
      ]);

      // Add the table data starting after the summary
      XLSX.utils.sheet_add_json(ws, exportData, { origin: -1, skipHeader: false });

      // Set column widths
      const wscols = [
        { wch: 5 },  // S.No
        { wch: 15 }, // Order Number
        { wch: 30 }, // Supplier
        { wch: 12 }, // Order Date
        { wch: 15 }, // Invoice Number
        { wch: 12 }, // Invoice Date
        { wch: 15 }, // Amount
        { wch: 10 }  // Status
      ];
      ws['!cols'] = wscols;

      // Add the worksheet to the workbook
      XLSX.utils.book_append_sheet(wb, ws, 'Purchase Report');

      // Generate Excel file
      const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
      const data = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });

      // Create download link
      const url = window.URL.createObjectURL(data);
      const link = document.createElement('a');
      link.href = url;
      link.download = `purchase_report_${format(new Date(), 'yyyy-MM-dd')}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.success('Purchase report exported successfully');
    } catch (error) {
      console.error('Error exporting purchase report:', error);
      toast.error('Failed to export purchase report');
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <Layout>
      <Box component="main" sx={{ py: 2 }}>
        <Container maxWidth="xl" sx={reportPageSx.container}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1.5, mb: 1.5 }}>
            <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />}>
              <Link
                component="button"
                variant="body2"
                onClick={() => navigate('/reports')}
                sx={{ display: 'flex', alignItems: 'center' }}
              >
                <ArrowBackIcon sx={{ mr: 0.5 }} fontSize="inherit" />
                Reports
              </Link>
              <Typography color="text.primary" variant="body2">Purchase Report</Typography>
            </Breadcrumbs>
            <Button
              variant="contained"
              size="small"
              onClick={handleExportToExcel}
              disabled={isExporting || filteredPurchaseDetails.length === 0}
              startIcon={isExporting ? <CircularProgress size={18} color="inherit" /> : <FileDownloadIcon />}
            >
              {isExporting ? 'Exporting...' : 'Export to Excel'}
            </Button>
          </Box>

          <Typography variant="h4" sx={{ ...reportPageSx.pageTitle, display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <ShoppingCartIcon color="primary" />
            Purchase Report
          </Typography>

          {/* Filters */}
          <Card sx={reportPageSx.filterCard}>
            <CardContent sx={reportPageSx.filterCardContent}>
              <Grid
                container
                spacing={reportPageSx.filterGrid.spacing}
                alignItems="flex-end"
                sx={{ flexWrap: { xs: 'wrap', md: 'nowrap' } }}
              >
                <Grid item xs={12} sm={6} md={2} sx={{ minWidth: 0 }}>
                  <TextField
                    label="Start Date"
                    type="date"
                    value={format(startDate, 'yyyy-MM-dd')}
                    onChange={(e) => setStartDate(new Date(e.target.value))}
                    fullWidth
                    size="small"
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={2} sx={{ minWidth: 0 }}>
                  <TextField
                    label="End Date"
                    type="date"
                    value={format(endDate, 'yyyy-MM-dd')}
                    onChange={(e) => setEndDate(new Date(e.target.value))}
                    fullWidth
                    size="small"
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={5} sx={{ minWidth: 0 }}>
                  <Autocomplete
                    size="small"
                    value={selectedSupplier || null}
                    onChange={handleSupplierChange}
                    options={Array.isArray(suppliers) ? suppliers : []}
                    getOptionLabel={(option) => {
                      if (!option) return '';
                      const name = option.name || '';
                      const phone = option.phone ? ` (${option.phone})` : '';
                      return `${name}${phone}`;
                    }}
                    isOptionEqualToValue={(option, value) => option?.id === value?.id}
                    renderOption={(props, option) => (
                      <li {...props} key={option.id}>
                        {(option.name || '') + (option.phone ? ` • ${option.phone}` : '')}
                      </li>
                    )}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Filter by Supplier"
                        placeholder="Search supplier..."
                        fullWidth
                        size="small"
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3} sx={{ minWidth: 0, flexShrink: 0 }}>
                  <Button
                    variant="contained"
                    onClick={handleGenerateReport}
                    disabled={loading}
                    fullWidth
                    sx={{ ...reportPageSx.filterButton, whiteSpace: 'nowrap' }}
                  >
                    {loading ? <CircularProgress size={20} color="inherit" /> : 'Generate Report'}
                  </Button>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Summary metrics */}
          <Grid container spacing={reportPageSx.metricsGrid.spacing} sx={reportPageSx.metricsGrid}>
            <Grid item xs={6} sm={3}>
              <ReportMetricCard
                label="Total Orders"
                value={purchaseReport?.totalOrders || 0}
                icon={<ReceiptIcon />}
                accentColor="primary.main"
              />
            </Grid>
            <Grid item xs={6} sm={3}>
              <ReportMetricCard
                label="Total Purchases"
                value={formatCurrency(purchaseReport?.totalPurchases || 0)}
                icon={<CurrencyRupeeIcon />}
                accentColor="success.main"
                valueColor="success.main"
              />
            </Grid>
            <Grid item xs={6} sm={3}>
              <ReportMetricCard
                label="Total Returns"
                value={formatCurrency(purchaseReport?.totalReturns || 0)}
                icon={<ReplayIcon />}
                accentColor="warning.main"
                valueColor="warning.main"
              />
            </Grid>
            <Grid item xs={6} sm={3}>
              <ReportMetricCard
                label="Net Spent"
                value={formatCurrency(purchaseReport?.totalSpent || 0)}
                icon={<CurrencyRupeeIcon />}
                accentColor="info.main"
                valueColor="info.main"
              />
            </Grid>
          </Grid>

          {/* Purchase Details Table */}
          <Card sx={reportPageSx.sectionCard}>
            <CardContent sx={reportPageSx.sectionCardContent}>
              <Typography sx={reportPageSx.sectionTitle}>Purchase Details</Typography>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>S.No.</TableCell>
                          <TableCell>Order #</TableCell>
                          <TableCell>Supplier</TableCell>
                          <TableCell>Order Date</TableCell>
                          <TableCell>Invoice #</TableCell>
                          <TableCell>Invoice Date</TableCell>
                          <TableCell align="right">Amount</TableCell>
                          <TableCell>Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {loading ? (
                          <TableRow>
                            <TableCell colSpan={8} align="center">
                              <CircularProgress />
                            </TableCell>
                          </TableRow>
                        ) : filteredPurchaseDetails.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={8} align="center">
                              {selectedSupplier
                                ? `No purchase records found for supplier ${selectedSupplier.name}`
                                : 'No purchase records found'}
                            </TableCell>
                          </TableRow>
                        ) : (
                          filteredPurchaseDetails
                            .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                            .map((order, index) => (
                              <React.Fragment key={order.id}>
                                <TableRow>
                                  <TableCell>{page * rowsPerPage + index + 1}</TableCell>
                                  <TableCell>{order.orderNumber}</TableCell>
                                  <TableCell>{order.supplier?.name || '-'}</TableCell>
                                  <TableCell>
                                    {order.orderDate ? format(new Date(order.orderDate), 'dd/MM/yyyy') : '-'}
                                  </TableCell>
                                  <TableCell>{order.invoiceNumber || '-'}</TableCell>
                                  <TableCell>
                                    {order.invoiceDate ? format(new Date(order.invoiceDate), 'dd/MM/yyyy') : '-'}
                                  </TableCell>
                                  <TableCell align="right">{formatCurrency(order.finalAmount || order.total || 0)}</TableCell>
                                  <TableCell>
                                    <IconButton
                                      size="small"
                                      onClick={() => handleViewOrder(order.id)}
                                      title="View Order Details"
                                    >
                                      <VisibilityIcon fontSize="small" />
                                    </IconButton>
                                  </TableCell>
                                </TableRow>
                                {expandedOrderId === order.id && selectedOrder && (
                                  <TableRow>
                                    <TableCell
                                      colSpan={8}
                                      sx={{
                                        py: 2,
                                        backgroundColor: 'background.default'
                                      }}
                                    >
                                      <Box
                                        sx={{
                                          p: 2,
                                          border: '1px solid',
                                          borderColor: 'divider',
                                          borderRadius: 1,
                                          bgcolor: 'background.paper'
                                        }}
                                      >
                                        <Grid container spacing={3}>
                                          {/* Order Information */}
                                          <Grid item xs={12} md={4}>
                                            <Card elevation={0} sx={{ height: '100%' }}>
                                              <CardContent>
                                                <Typography variant="h6" gutterBottom>
                                                  Order Information
                                                </Typography>
                                                <List dense>
                                                  <ListItem>
                                                    <ListItemText
                                                      primary="Order Number"
                                                      secondary={selectedOrder.orderNumber}
                                                    />
                                                  </ListItem>
                                                  <ListItem>
                                                    <ListItemText
                                                      primary="Order Date"
                                                      secondary={format(new Date(selectedOrder.orderDate), 'dd/MM/yyyy')}
                                                    />
                                                  </ListItem>
                                                  <ListItem>
                                                    <ListItemText
                                                      primary="Status"
                                                      secondary={
                                                        <Box component="span" sx={{ display: 'inline-block' }}>
                                                          <Chip
                                                            label={selectedOrder.status}
                                                            color={getStatusColor(selectedOrder.status)}
                                                            size="small"
                                                          />
                                                        </Box>
                                                      }
                                                    />
                                                  </ListItem>
                                                  <ListItem>
                                                    <ListItemText
                                                      primary="Invoice Number"
                                                      secondary={selectedOrder.invoiceNumber || selectedOrder.invoiceNo || 'N/A'}
                                                    />
                                                  </ListItem>
                                                  <ListItem>
                                                    <ListItemText
                                                      primary="Invoice Date"
                                                      secondary={selectedOrder.invoiceDate ? format(new Date(selectedOrder.invoiceDate), 'dd/MM/yyyy') : 'N/A'}
                                                    />
                                                  </ListItem>
                                                </List>
                                              </CardContent>
                                            </Card>
                                          </Grid>

                                          {/* Supplier Information */}
                                          <Grid item xs={12} md={4}>
                                            <Card elevation={0} sx={{ height: '100%' }}>
                                              <CardContent>
                                                <Typography variant="h6" gutterBottom>
                                                  Supplier Information
                                                </Typography>
                                                <List dense>
                                                  <ListItem>
                                                    <ListItemText
                                                      primary="Name"
                                                      secondary={selectedOrder.supplierName || 'N/A'}
                                                    />
                                                  </ListItem>
                                                  <ListItem>
                                                    <ListItemText
                                                      primary="Contact"
                                                      secondary={selectedOrder.supplierPhone || 'N/A'}
                                                    />
                                                  </ListItem>
                                                  <ListItem>
                                                    <ListItemText
                                                      primary="Email"
                                                      secondary={selectedOrder.supplierEmail || 'N/A'}
                                                    />
                                                  </ListItem>
                                                  <ListItem>
                                                    <ListItemText
                                                      primary="Address"
                                                      secondary={selectedOrder.supplierAddress || 'N/A'}
                                                    />
                                                  </ListItem>
                                                  <ListItem>
                                                    <ListItemText
                                                      primary="GST Number"
                                                      secondary={selectedOrder.supplierGstNumber || 'N/A'}
                                                    />
                                                  </ListItem>
                                                </List>
                                              </CardContent>
                                            </Card>
                                          </Grid>

                                          {/* Order Summary */}
                                          <Grid item xs={12} md={4}>
                                            <Card elevation={0} sx={{ height: '100%' }}>
                                              <CardContent>
                                                <Typography variant="h6" gutterBottom>
                                                  Order Summary
                                                </Typography>
                                                <List dense>
                                                  <ListItem>
                                                    <ListItemText
                                                      primary="Subtotal"
                                                      secondary={formatCurrency(Number(selectedOrder.subtotal))}
                                                    />
                                                  </ListItem>
                                                  <ListItem>
                                                    <ListItemText
                                                      primary="Tax (18%)"
                                                      secondary={formatCurrency(Number(selectedOrder.tax))}
                                                    />
                                                  </ListItem>
                                                  <ListItem>
                                                    <ListItemText
                                                      primary="Discount"
                                                      secondary={formatCurrency(Number(selectedOrder.discount))}
                                                    />
                                                  </ListItem>
                                                  <ListItem>
                                                    <ListItemText
                                                      primary={<Typography variant="subtitle1" fontWeight="bold">Total Amount</Typography>}
                                                      secondary={<Typography variant="h6" color="primary">{formatCurrency(Number(selectedOrder.totalAmount))}</Typography>}
                                                    />
                                                  </ListItem>
                                                </List>
                                              </CardContent>
                                            </Card>
                                          </Grid>

                                          {/* Products Table */}
                                          <Grid item xs={12}>
                                            <Card elevation={0}>
                                              <CardContent>
                                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                                  <Typography variant="h6">
                                                    Order Items
                                                  </Typography>
                                                  <Button
                                                    variant="contained"
                                                    color="primary"
                                                    onClick={() => handlePrintInvoice(selectedOrder.id)}
                                                    startIcon={<PrintIcon />}
                                                    size="small"
                                                  >
                                                    Print Invoice
                                                  </Button>
                                                </Box>
                                                <TableContainer>
                                                  <Table size="small">
                                                    <TableHead>
                                                      <TableRow>
                                                        <TableCell>S.No.</TableCell>
                                                        <TableCell>Product</TableCell>
                                                        <TableCell align="right">Quantity</TableCell>
                                                        <TableCell align="right">Unit Price</TableCell>
                                                        <TableCell align="right">Total</TableCell>
                                                      </TableRow>
                                                    </TableHead>
                                                    <TableBody>
                                                      {selectedOrder.items?.map((item, index) => (
                                                        <TableRow key={item.id || index}>
                                                          <TableCell>{index + 1}</TableCell>
                                                          <TableCell>{item.productName || 'N/A'}</TableCell>
                                                          <TableCell align="right">{item.quantity || 0}</TableCell>
                                                          <TableCell align="right">{formatCurrency(item.price || 0)}</TableCell>
                                                          <TableCell align="right">{formatCurrency(item.total || 0)}</TableCell>
                                                        </TableRow>
                                                      ))}
                                                      {(!selectedOrder.items || selectedOrder.items.length === 0) && (
                                                        <TableRow>
                                                          <TableCell colSpan={5} align="center">
                                                            No items found
                                                          </TableCell>
                                                        </TableRow>
                                                      )}
                                                    </TableBody>
                                                  </Table>
                                                </TableContainer>
                                              </CardContent>
                                            </Card>
                                          </Grid>
                                        </Grid>
                                      </Box>
                                    </TableCell>
                                  </TableRow>
                                )}
                              </React.Fragment>
                            ))
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  <TablePagination
                    component="div"
                    count={filteredPurchaseDetails.length}
                    page={page}
                    onPageChange={handleChangePage}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    size="small"
                  />
            </CardContent>
          </Card>
        </Container>
      </Box>

      {/* Order Details Dialog */}
      <Dialog
        open={Boolean(selectedOrder)}
        onClose={handleCloseOrderDetails}
        maxWidth="md"
        fullWidth
      >
        {selectedOrder && (
          <>
            <DialogTitle>
              <Stack direction="row" alignItems="center" spacing={1}>
                <ReceiptIcon />
                <Typography variant="h6">
                  Purchase Order #{selectedOrder.orderNumber}
                </Typography>
              </Stack>
            </DialogTitle>
            <DialogContent dividers>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Supplier Details
                  </Typography>
                  <Typography variant="body1">{selectedOrder.supplierName}</Typography>
                  <Typography variant="body2" color="textSecondary">
                    {selectedOrder.supplierEmail}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    {selectedOrder.supplierPhone}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Order Details
                  </Typography>
                  <Typography variant="body1">
                    Date: {format(new Date(selectedOrder.orderDate), 'dd/MM/yyyy')}
                  </Typography>
                  <Typography variant="body1">
                    Status:{' '}
                    <Chip
                      label={selectedOrder.status}
                      color={getStatusColor(selectedOrder.status)}
                      size="small"
                    />
                  </Typography>
                  <Typography variant="body1">
                    Invoice #: {selectedOrder.invoiceNumber || selectedOrder.invoiceNo || 'N/A'}
                  </Typography>
                  <Typography variant="body1">
                    Invoice Date:{' '}
                    {selectedOrder.invoiceDate &&
                      format(new Date(selectedOrder.invoiceDate), 'dd/MM/yyyy')}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                    Order Items
                  </Typography>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Product</TableCell>
                          <TableCell align="right">Quantity</TableCell>
                          <TableCell align="right">Price</TableCell>
                          <TableCell align="right">Total</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {selectedOrder.items?.map((item) => (
                          <TableRow key={item.id}>
                            <TableCell>{item.productName}</TableCell>
                            <TableCell align="right">{item.quantity}</TableCell>
                            <TableCell align="right">{formatCurrency(item.price)}</TableCell>
                            <TableCell align="right">{formatCurrency(item.total)}</TableCell>
                          </TableRow>
                        ))}
                        <TableRow>
                          <TableCell colSpan={3} align="right">
                            <strong>Subtotal</strong>
                          </TableCell>
                          <TableCell align="right">
                            {formatCurrency(selectedOrder.subtotal)}
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell colSpan={3} align="right">
                            <strong>Tax</strong>
                          </TableCell>
                          <TableCell align="right">
                            {formatCurrency(selectedOrder.tax)}
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell colSpan={3} align="right">
                            <strong>Total</strong>
                          </TableCell>
                          <TableCell align="right">
                            {formatCurrency(selectedOrder.totalAmount)}
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleCloseOrderDetails}>Close</Button>
              <Button
                variant="contained"
                startIcon={<PrintIcon />}
                onClick={() => handlePrintInvoice(selectedOrder.id)}
              >
                Print Invoice
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Layout>
  );
};

export default PurchaseReport; 
